// ---------------------------------------------------------------------------
// R1 — "the project has a design system → generation follows it".
//
// `functions/api/design-system/project-registration.ts` already turns an
// imported .fig into a durable, project-scoped, rights-verified record
// (`settings.projectDesignSystem`). Nothing read it: generation still refused
// every project with an active source workspace, so the registration moved a
// counter and changed no pixel. This module is the read half.
//
// ─── Why this is a binding and not a new authority ────────────────────────
// The record's `authority` block is COPIED from the rights declaration, whose
// schema pins `productionGenerationAllowed`, `packPromotionAllowed`,
// `retrievalCorpusIngestionAllowed`, `ontologyAdmissionAllowed` to the literal
// `false` (packages/cogo-schema/v4/openpencil-source-rights-declaration.
// cogo.schema.json:131-141). The ONE authority it grants is
// `previewGenerationAllowed: true`. This module exercises exactly that and
// nothing else:
//   • it projects the record's already-derived `designSystemTokens`/`themeHint`
//     through the SAME seam a verified pack release uses
//     (`withDesignSystemPackReleaseGenerationAuthority`), so no new generation
//     path is introduced;
//   • it emits a lineage whose `previewOnly` / `productionGenerationAllowed`
//     fields are literal types, so widening one is a type error rather than a
//     config change;
//   • it never reaches the retrieval corpus, the pack store, or the ontology.
//
// ─── The drift rule ──────────────────────────────────────────────────────
// A registration is evidence about ONE exact source revision. The live
// workspace can move underneath it (accept another token, re-import, restore a
// different file), and a stale binding would let a generation claim to follow a
// design it no longer matches. So identity is re-checked against the LIVE
// workspace on every bind and any mismatch fails closed with a code the caller
// turns into an instruction the user can act on ("re-register"), never a
// silent fallback to default colours.
// ---------------------------------------------------------------------------

import type { ProjectDesignSystemRegistrationSummary } from './register-project-design-system'
import type { OpenPencilSourceWorkspace } from '@/features/openpencil-figma/openpencil-source-workspace'

/** Settings key carrying the lineage of a source-bound generation, at birth. */
export const PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY =
  'projectDesignSystemGeneration' as const

export const PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION =
  'cogo.studio-project-design-system-generation-lineage.v1' as const

/**
 * Renderability floor — identical to the registration's and the pack
 * release binding's. The server already refuses to register without it; this
 * is the read-side restatement so a hand-edited settings blob cannot smuggle a
 * floorless record into generation.
 */
export const PROJECT_DESIGN_SYSTEM_GENERATION_RENDER_FLOOR = [
  'primary',
  'background',
  'foreground',
] as const

/**
 * Durable lineage persisted on the GENERATED project's settings row at birth.
 *
 * This is where the preview-only label lives. The registration's authority
 * ceiling is not decorative precisely because this record travels with the
 * project the generation created: `publishProject` reads it and refuses
 * (services/project-api.ts), and it survives a browser that dies mid-run
 * because it is written by `createServerProject`, not after success.
 */
export interface StudioProjectDesignSystemGenerationLineage {
  schemaVersion: typeof PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION
  source: 'project-design-system-registration'
  designSystemId: string
  /** The project that OWNS the imported source (not the generated project). */
  sourceProjectId: string
  sourceId: string
  fileSha256: string
  sourceRevision: number
  workspaceRevision: number
  registrationContentHash: string
  projectedTokenCount: number
  /** Literal — the rights ceiling this generation was produced under. */
  previewOnly: true
  productionGenerationAllowed: false
  packPromotionAllowed: false
}

/** A drift-checked, rights-ceilinged design system bound for one generation. */
export interface StudioProjectDesignSystemGenerationBinding {
  schemaVersion: 'cogo.studio-project-design-system-generation-binding.v1'
  designSystemId: string
  /** Kebab CSS-var keys → CSS color values (the existing generation seam). */
  designSystemTokens: Record<string, string>
  themeHint: { primaryColor: string; style: string }
  lineage: StudioProjectDesignSystemGenerationLineage
}

export type ProjectDesignSystemGenerationBindingErrorCode =
  | 'project_design_system_not_registered'
  | 'project_design_system_source_drift'
  | 'project_design_system_missing_render_floor'
  | 'project_design_system_authority_insufficient'

export class ProjectDesignSystemGenerationBindingError extends Error {
  override readonly name = 'ProjectDesignSystemGenerationBindingError'

  constructor(
    readonly code: ProjectDesignSystemGenerationBindingErrorCode,
    detail?: string,
  ) {
    super(detail ? `${code}: ${detail}` : code)
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function sourceRevisionOf(workspace: OpenPencilSourceWorkspace): number {
  const lineage = workspace.bundle.revisionLineage
  const revision = lineage?.sourceRevision
  return Number.isInteger(revision) && (revision as number) >= 0
    ? (revision as number)
    : 0
}

/**
 * Read a registration back out of a project settings document.
 *
 * Structural only — identity assurance is `resolveProjectDesignSystemGeneration
 * Binding`'s job, which is the single place the live workspace is consulted.
 */
export function readProjectDesignSystemRegistrationFromSettings(
  settings: unknown,
): ProjectDesignSystemRegistrationSummary | null {
  const value = isRecord(settings) ? settings.projectDesignSystem : undefined
  if (!isRecord(value)) return null
  if (value.schemaVersion !== 'cogo.project-design-system-registration.v1') {
    return null
  }
  if (typeof value.designSystemId !== 'string' || !value.designSystemId) {
    return null
  }
  if (!isRecord(value.designSystemTokens) || !isRecord(value.source)) return null
  return value as unknown as ProjectDesignSystemRegistrationSummary
}

/**
 * Bind a registered project design system to one generation, fail-closed.
 *
 * Returns `null` ONLY when there is nothing registered at all — that is the
 * "not built yet for this project" case the caller reports as an instruction.
 * Every other refusal throws a typed code, because a registration that exists
 * but does not match is a drift signal, not an absence.
 */
export function resolveProjectDesignSystemGenerationBinding(input: {
  registration: ProjectDesignSystemRegistrationSummary | null | undefined
  workspace: OpenPencilSourceWorkspace | null | undefined
  /** The project the source workspace belongs to. */
  sourceProjectId: string | null | undefined
}): StudioProjectDesignSystemGenerationBinding | null {
  const { registration, workspace } = input
  if (!registration || !workspace) return null

  // Identity: the record must attest the EXACT source revision now live.
  // `workspaceRevision` is included deliberately — accepting one more token
  // bumps it, and a design system that predates that decision is no longer
  // the design the user is looking at.
  const liveSourceRevision = sourceRevisionOf(workspace)
  if (
    registration.source.sourceId !== workspace.sourceId ||
    registration.source.fileSha256 !== workspace.bundle.fileSha256 ||
    registration.source.sourceRevision !== liveSourceRevision ||
    registration.source.workspaceRevision !== workspace.revision
  ) {
    throw new ProjectDesignSystemGenerationBindingError(
      'project_design_system_source_drift',
      'the registered design system does not attest the source revision now open',
    )
  }
  if (
    registration.projectId !== input.sourceProjectId ||
    !input.sourceProjectId
  ) {
    throw new ProjectDesignSystemGenerationBindingError(
      'project_design_system_source_drift',
      'the registered design system belongs to a different project',
    )
  }

  // Authority: mirror, never widen. A record whose ceiling was tampered with
  // (hand-edited settings, a future schema that relaxed a literal) must not
  // reach generation just because the shape still parses.
  const authority = registration.authority
  if (
    !authority ||
    authority.scope !== 'project-only' ||
    authority.previewGenerationAllowed !== true ||
    authority.productionGenerationAllowed !== false ||
    authority.packPromotionAllowed !== false ||
    authority.retrievalCorpusIngestionAllowed !== false ||
    authority.ontologyAdmissionAllowed !== false ||
    authority.redistributionAllowed !== false ||
    authority.modelMayAuthor !== false
  ) {
    throw new ProjectDesignSystemGenerationBindingError(
      'project_design_system_authority_insufficient',
      'the registered design system does not carry the project-only preview ceiling',
    )
  }

  const designSystemTokens: Record<string, string> = {}
  for (const [key, value] of Object.entries(
    registration.designSystemTokens ?? {},
  )) {
    if (typeof value === 'string' && value.trim()) {
      designSystemTokens[key] = value.trim()
    }
  }
  const missingFloor = PROJECT_DESIGN_SYSTEM_GENERATION_RENDER_FLOOR.filter(
    (key) => designSystemTokens[key] === undefined,
  )
  if (missingFloor.length > 0) {
    throw new ProjectDesignSystemGenerationBindingError(
      'project_design_system_missing_render_floor',
      missingFloor.join(','),
    )
  }

  const lineage: StudioProjectDesignSystemGenerationLineage = {
    schemaVersion: PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION,
    source: 'project-design-system-registration',
    designSystemId: registration.designSystemId,
    sourceProjectId: input.sourceProjectId,
    sourceId: registration.source.sourceId,
    fileSha256: registration.source.fileSha256,
    sourceRevision: registration.source.sourceRevision,
    workspaceRevision: registration.source.workspaceRevision,
    registrationContentHash: registration.contentHash,
    projectedTokenCount: Object.keys(designSystemTokens).length,
    previewOnly: true,
    productionGenerationAllowed: false,
    packPromotionAllowed: false,
  }

  return {
    schemaVersion: 'cogo.studio-project-design-system-generation-binding.v1',
    designSystemId: registration.designSystemId,
    designSystemTokens,
    themeHint: {
      primaryColor: designSystemTokens.primary!,
      style: registration.themeHint?.style ?? `source:${registration.source.sourceId}`,
    },
    lineage,
  }
}

/**
 * Overlay the bound source tokens as the plan's presentation authority.
 *
 * THREE fields, and the third one is the point:
 *
 *  • `themeHint` / `designSystemTokens` — the same two fields
 *    `withDesignSystemPackReleaseGenerationAuthority` writes
 *    (design-system-pack-release-binding.ts:318), which drive the LOCAL
 *    renderer: requestAppGeneration copies `designSystemTokens` into
 *    `project.tokens`, which becomes the preview's CSS variables.
 *
 *  • `designSystem` — measured 2026-07-30 against the deployed generator:
 *    `designSystemTokens` NEVER reaches the model. The job lane's allowlist
 *    rebuild (cogo-platform app-generation-spec-validation.ts) discards it
 *    outright, and no prompt builder reads it. The ONE token channel the
 *    generator actually consumes is `designSystem` — a flat
 *    `Record<string,string>` of CSS-var names, read at intake
 *    (cogo-cloudflare routes/app-v2.ts:1262) and rendered into the block
 *    prompt as "### Available Design Tokens (CSS Variables)" by
 *    `buildDesignSystemPromptSection` (lib/block-generator-v2.ts:1105-1117),
 *    alongside `buildDesignBrief`. `themeHint.primaryColor`/`.style` also
 *    reach the prompt directly ("Theme: primary …, style: …", :1300).
 *
 *    So without `designSystem`, binding a source design system would change
 *    only the CSS variables wrapped around model output that had never heard
 *    of the source. Setting it is what makes the generated blocks authored
 *    against the imported design rather than merely repainted afterwards.
 *
 * Non-mutating.
 */
export function withProjectDesignSystemGenerationAuthority<
  T extends { appSpec: object },
>(plan: T, binding: StudioProjectDesignSystemGenerationBinding): T {
  const fields = designSystemGenerationAuthorityFields(binding)
  return {
    ...plan,
    appSpec: {
      ...plan.appSpec,
      ...fields,
    },
    themeHint: fields.themeHint,
    designSystemTokens: fields.designSystemTokens,
  } as T
}

export function designSystemGenerationAuthorityFields(binding: {
  themeHint: { primaryColor: string; style: string; fontFamily?: string }
  designSystemTokens: Record<string, string>
}): {
  themeHint: { primaryColor: string; style: string; fontFamily?: string }
  designSystemTokens: Record<string, string>
  designSystem: Record<string, string>
} {
  return {
    themeHint: { ...binding.themeHint },
    designSystemTokens: { ...binding.designSystemTokens },
    designSystem: { ...binding.designSystemTokens },
  }
}

export class DesignSystemGenerationProjectionError extends Error {
  override readonly name = 'DesignSystemGenerationProjectionError'
}

function requiresDesignSystemStyleInjection(contract: unknown): boolean {
  if (!isRecord(contract)) return false
  const admission = contract.blockAdmission
  return (
    isRecord(admission) &&
    admission.requireDesignSystemStyleInjection === true
  )
}

function stringProjection(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) return null
  const projection: Record<string, string> = {}
  for (const [key, token] of Object.entries(value)) {
    if (typeof token !== 'string') return null
    projection[key] = token
  }
  return Object.keys(projection).length > 0 ? projection : null
}

function canonicalProjection(value: Record<string, string>): string {
  return JSON.stringify(
    Object.entries(value).sort(([left], [right]) => left.localeCompare(right)),
  )
}

/**
 * Enforce the contract's design-system injection promise at the last shared
 * generation boundary. A contract that requires injection may never travel
 * with only the renderer projection or only the generator projection, and
 * the two projections must be byte-equivalent after key ordering.
 */
export function assertDesignSystemGenerationProjection(input: {
  designSystemContract?: unknown
  designSystemTokens?: unknown
  designSystem?: unknown
}): void {
  if (!requiresDesignSystemStyleInjection(input.designSystemContract)) return

  const renderer = stringProjection(input.designSystemTokens)
  const generator = stringProjection(input.designSystem)
  if (!renderer || !generator) {
    throw new DesignSystemGenerationProjectionError(
      'Design-system injection is required, but the renderer and generator projections are not both present.',
    )
  }
  if (canonicalProjection(renderer) !== canonicalProjection(generator)) {
    throw new DesignSystemGenerationProjectionError(
      'Design-system injection is required, but the renderer and generator projections do not match.',
    )
  }
}

/**
 * User-legible wording for every way a source-bound generation can refuse.
 *
 * Each one names the control that fixes it. The old behaviour ("start a new
 * project without importing a source") was accurate but useless: it told the
 * user to abandon the thing they had just imported. These say what to do with
 * it instead. No internal term, no error-class name, no code leaks to screen.
 */
export function describeProjectDesignSystemGenerationBlocker(
  code: ProjectDesignSystemGenerationBindingErrorCode,
): string {
  switch (code) {
    case 'project_design_system_not_registered':
      return (
        'This project has an imported design (.fig) that has not been applied yet, ' +
        'and Studio will not generate an app that ignores it. Open Design source → ' +
        'Tokens, give at least the main, background and text colours their role, ' +
        'then choose “Register as this project’s design system” and generate again.'
      )
    case 'project_design_system_source_drift':
      return (
        'The imported design changed after its design system was applied, so ' +
        'generating now would follow colours the design no longer uses. Open ' +
        'Design source → Tokens and register the design system again, then generate.'
      )
    case 'project_design_system_missing_render_floor':
      return (
        'The applied design system is missing one of the three colours every ' +
        'screen needs — main, background and text. Give those roles to accepted ' +
        'tokens in Design source → Tokens, register again, then generate.'
      )
    case 'project_design_system_authority_insufficient':
      return (
        'The rights confirmed for this imported design do not allow generating ' +
        'from it. Re-confirm the source rights for this exact file revision in ' +
        'Design source, then try again.'
      )
  }
}

/** Merge the lineage under its own settings key, composing with siblings. */
export function withProjectDesignSystemGenerationLineage(
  existingSettings: unknown,
  lineage: StudioProjectDesignSystemGenerationLineage,
): Record<string, unknown> {
  const settings = isRecord(existingSettings) ? { ...existingSettings } : {}
  settings[PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY] = lineage
  return settings
}

/**
 * Read the birth lineage back off a generated project's settings.
 *
 * The publish gate calls this: a project generated under a preview-only
 * source ceiling must not become a public deployment just because the
 * generation itself succeeded.
 */
export function readProjectDesignSystemGenerationLineage(
  settings: unknown,
): StudioProjectDesignSystemGenerationLineage | null {
  const value = isRecord(settings)
    ? settings[PROJECT_DESIGN_SYSTEM_GENERATION_SETTINGS_KEY]
    : undefined
  if (!isRecord(value)) return null
  if (
    value.schemaVersion !== PROJECT_DESIGN_SYSTEM_GENERATION_LINEAGE_SCHEMA_VERSION
  ) {
    return null
  }
  if (typeof value.designSystemId !== 'string' || !value.designSystemId) {
    return null
  }
  return value as unknown as StudioProjectDesignSystemGenerationLineage
}
